import { DEFAULT_PRICING_RULES, type PricingRuleValues } from '@repo/db/pricing-rules'
/**
 * A stored line, resolved against the live catalogue.
 *
 * Three outcomes and the third one is why this file exists. A basket now lives
 * for thirty days, so "the offering behind this line is gone" stops being an
 * edge case: today such a line vanishes silently from the screen
 * (`if (!offering) return []`) while `resolveCheckoutLines` still refuses the
 * WHOLE order over it — an order refused for a reason nobody can see. It gets
 * its own status and its own sentence instead.
 */
import {
  applyPrefill,
  type CreatorOfferingView,
  type CreatorPrefill,
  type CreatorVariantView,
  type PickField,
  pickIssue,
  pickPieces,
  pricingProblem,
  toCartRef,
} from '@/lib/creator'
import { type CartLineRef, cartLineId, type OrderLine } from '@/lib/order-line'
import { type LineMoney, lineMoney, type PricedLine } from '@/lib/order-money'

export type LineView =
  /**
   * Being filled in. It is in the basket and it is not orderable yet — which is
   * NOT the same as having no price.
   *
   * A line whose variant and quantity are chosen costs what it costs; the empty
   * brief box beside it stops the ORDER, not the arithmetic. So a draft carries
   * `money` as soon as `pricingProblem` clears, and `null` only while the
   * amount is genuinely unknowable. Running the two together is what made the
   * summary sit still while the customer watched a price appear on the card.
   */
  | {
      status: 'draft'
      line: OrderLine
      offering: CreatorOfferingView
      variant: CreatorVariantView | undefined
      problem: string
      /**
       * Which control `problem` is about, so the card can mark it and print the
       * sentence under it. `null` only for the generic fallback below, which no
       * `pickIssue` branch produces.
       */
      field: PickField | null
      lineKey: null
      /** Positions toward spec §13's coordinator; 0 while nothing is priceable. */
      pieces: number
      money: LineMoney | null
    }
  /** Complete. Has a signature, a reference and a figure. */
  | {
      status: 'ready'
      line: OrderLine
      offering: CreatorOfferingView
      variant: CreatorVariantView
      offeringSlug: string
      /** Positions for spec §13's coordinator threshold — see `pickPieces`. */
      pieces: number
      problem: null
      lineKey: string
      ref: CartLineRef
      money: LineMoney
    }
  /** The offering left the catalogue under it. Nothing to configure, only to remove. */
  | { status: 'orphan'; line: OrderLine; problem: string }

const GONE = 'Wycofaliśmy tę pozycję z oferty — usuwamy ją z koszyka.'

export function viewLine(
  line: OrderLine,
  bySlug: Map<string, CreatorOfferingView>,
  rules: PricingRuleValues = DEFAULT_PRICING_RULES,
): LineView {
  const offering = bySlug.get(line.offeringSlug)
  if (!offering) return { status: 'orphan', line, problem: GONE }

  const variant = offering.variants.find((v) => v.slug === line.pick.variantSlug)
  const issue = pickIssue(offering, line.pick)
  const problem = issue?.message ?? null
  const ref = toCartRef(offering, line.pick)

  // `toCartRef` gates on the same `pickProblem`, so the two agree by
  // construction — the null check is what convinces the compiler, not a second
  // opinion about completeness.
  if (problem || !variant || !ref) {
    // Priced as far as it can be. `pricingProblem` is a strict subset of
    // `pickProblem`, so a line that clears it has a variant — the `!variant`
    // arm above is what convinces the compiler, not a second opinion.
    const priceable = variant && !pricingProblem(offering, line.pick)
    return {
      status: 'draft',
      line,
      offering,
      variant,
      problem: problem ?? 'Uzupełnij tę pozycję.',
      field: issue?.field ?? null,
      lineKey: null,
      pieces: priceable ? pickPieces(offering, line.pick) : 0,
      money: priceable ? lineMoney(offering, variant, line.pick, rules) : null,
    }
  }

  return {
    status: 'ready',
    line,
    offering,
    variant,
    offeringSlug: offering.slug,
    pieces: pickPieces(offering, line.pick),
    problem: null,
    lineKey: cartLineId(ref),
    ref,
    money: lineMoney(offering, variant, line.pick, rules),
  }
}

export function viewLines(
  lines: readonly OrderLine[],
  bySlug: Map<string, CreatorOfferingView>,
  rules: PricingRuleValues = DEFAULT_PRICING_RULES,
): LineView[] {
  return lines.map((line) => viewLine(line, bySlug, rules))
}

export function isReady(view: LineView): view is Extract<LineView, { status: 'ready' }> {
  return view.status === 'ready'
}

/**
 * Everything in the basket that has an amount, ready for `orderTotals`.
 *
 * Deliberately NOT `views.filter(isReady)`, which is what this used to be. The
 * total is a sum of PRICES, and a price does not wait for a brief to be typed —
 * the customer can see the rate on the card, so a summary that ignores it is a
 * summary that visibly does not add up.
 *
 * An orphan contributes nothing: its offering left the catalogue, so there is
 * no rate to read and the line has to be removed before the order can go
 * anywhere.
 */
export function pricedLines(views: readonly LineView[]): PricedLine[] {
  return views.flatMap((view) =>
    view.status !== 'orphan' && view.money
      ? [{ offeringSlug: view.offering.slug, money: view.money, pieces: view.pieces }]
      : [],
  )
}

/**
 * Where a deep link should land: on a line that already exists, or on a new one.
 *
 * The rule in one sentence — **a deep link is an instruction for the basket, not
 * a description of the screen.** It is consumed on the server exactly once and
 * disappears from the address, so a reload cannot add the same thing twice and a
 * shared link cannot keep re-adding it on every visit.
 *
 * `merge` — this exact configuration is already in the basket. Arriving from a
 * tier card to find the same package listed twice reads as a bug.
 * `fill` — a line for this offering is open and unfinished; the link sets it up
 * rather than opening a second one beside it.
 * `append` — nothing to attach to.
 */
export type PrefillTarget = { kind: 'merge' | 'fill'; lineId: string } | { kind: 'append' }

export function prefillTarget(
  prefill: CreatorPrefill,
  lines: readonly OrderLine[],
  bySlug: Map<string, CreatorOfferingView>,
): PrefillTarget | null {
  const offering = prefill.offeringSlug ? bySlug.get(prefill.offeringSlug) : undefined
  if (!offering) return null

  const pick = applyPrefill(offering, prefill)
  const ref = toCartRef(offering, pick)
  if (ref) {
    const key = cartLineId(ref)
    const twin = lines.find((line) => {
      const view = viewLine(line, bySlug)
      return view.status === 'ready' && view.lineKey === key
    })
    if (twin) return { kind: 'merge', lineId: twin.lineId }
  }

  const open = lines.find(
    (line) => line.offeringSlug === offering.slug && viewLine(line, bySlug).status === 'draft',
  )
  if (open) return { kind: 'fill', lineId: open.lineId }

  return { kind: 'append' }
}
